import { Zap, Shield, CheckCircle2 } from 'lucide-react'
import { Card, CardContent } from '@/ui/card'
import { cn } from '@/core/lib/utils'

import {
  LEVELS,
  LEVEL_INDEX,
  SKILL_ICONS,
  SKILL_LABELS,
  getSkills,
  type AgentConfig,
  type AgentSkills,
} from '../lib/config'

interface AgentOverviewTabProps {
  agent: AgentConfig
  updatingId: string | null
  onLevelChange: (cfg: AgentConfig, level: AgentConfig['autonomyLevel']) => void
  onSkillToggle: (cfg: AgentConfig, skill: keyof AgentSkills) => void
}

export function AgentOverviewTab({ agent, updatingId, onLevelChange, onSkillToggle }: AgentOverviewTabProps) {
  const currentIdx = LEVEL_INDEX[agent.autonomyLevel] ?? 0
  const skills = getSkills(agent.config)

  return (
    <div className="space-y-4">
      {/* Autonomy level */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            <h4 className="text-sm font-semibold text-navy">Autonomy Level</h4>
          </div>

          <div className="relative">
            <div className="flex items-center justify-between relative z-10">
              {LEVELS.map((level, idx) => {
                const Icon = level.icon
                const isActive = idx === currentIdx
                const isPast = idx < currentIdx
                return (
                  <button
                    key={level.value}
                    onClick={() => onLevelChange(agent, level.value)}
                    disabled={updatingId === agent.agentId}
                    className="group flex flex-col items-center gap-1.5 disabled:opacity-50"
                  >
                    <div
                      className={cn(
                        'w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all',
                        isActive
                          ? cn(level.bg, level.border, level.color)
                          : isPast
                            ? 'bg-primary border-primary text-white'
                            : 'bg-white border-gray-200 text-gray-300'
                      )}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className={cn('text-[10px] font-semibold uppercase', isActive ? level.color : isPast ? 'text-primary' : 'text-gray-300')}>
                      {level.shortLabel}
                    </span>
                  </button>
                )
              })}
            </div>
            <div className="absolute top-[18px] left-4 right-4 h-0.5 bg-gray-100">
              <div className="h-full bg-primary transition-all duration-500 rounded-full" style={{ width: `${(currentIdx / (LEVELS.length - 1)) * 100}%` }} />
            </div>
          </div>

          <div className={cn('rounded-lg border p-3 text-xs', LEVELS[currentIdx].border, LEVELS[currentIdx].bg)}>
            <p className="font-medium text-navy mb-1">{LEVELS[currentIdx].label}</p>
            <p className="text-slate-500 mb-2">{LEVELS[currentIdx].desc}</p>
            <div className="flex flex-wrap gap-2">
              {LEVELS[currentIdx].examples.map((ex, i) => (
                <span key={`ex-${i}-${ex}`} className="inline-flex items-center gap-1 bg-white/70 border border-black/5 rounded-md px-2 py-0.5 text-[10px] text-slate-500">
                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                  {ex}
                </span>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Skills grid */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            <h4 className="text-sm font-semibold text-navy">Permissions & Skills</h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(Object.keys(SKILL_LABELS) as Array<keyof AgentSkills>).map((key) => {
              const Icon = SKILL_ICONS[key]
              const enabled = skills[key]
              const { label, description } = SKILL_LABELS[key]
              return (
                <button
                  key={key}
                  onClick={() => onSkillToggle(agent, key)}
                  disabled={updatingId === agent.agentId}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-xl border text-left transition-all disabled:opacity-50',
                    enabled
                      ? 'bg-orange-50/50 border-primary/30'
                      : 'bg-white border-slate-150 hover:border-gray-300'
                  )}
                >
                  <div
                    className={cn(
                      'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
                      enabled ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'
                    )}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={cn('text-sm font-medium', enabled ? 'text-navy' : 'text-gray-400')}>{label}</p>
                      <div
                        className={cn(
                          'w-8 h-4 rounded-full transition-colors relative',
                          enabled ? 'bg-primary' : 'bg-gray-200'
                        )}
                      >
                        <span
                          className={cn(
                            'absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform',
                            enabled ? 'left-[18px]' : 'left-[2px]'
                          )}
                        />
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5">{description}</p>
                  </div>
                </button>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
